import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Button,
  Badge,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  PersonAdd as PersonAddIcon,
  Description as DescriptionIcon,
  ListAlt as ListAltIcon,
  Logout as LogoutIcon,
  Notifications as NotificationsIcon,
  Menu as MenuIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import OfficerHome from './OfficerHome';
import CustomerRegistration from './CustomerRegistration';
import CustomerManagement from './CustomerManagement';
import LoanApplication from './LoanApplication';
import LoanApplicationForExisting from './LoanApplicationForExisting';
import MyLoanApplications from './MyLoanApplications';
import LoanSchedule from './LoanSchedule';
import Notifications from './Notifications';
import LoansClosedByAdmin from './LoansClosedByAdmin';
import ActiveLoans from './ActiveLoans';
import RejectedLoans from './RejectedLoans';
import ClosedLoans from './ClosedLoans';
import PendingVerifiedLoans from './PendingVerifiedLoans';

const drawerWidth = 260;

const OfficerDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState('dashboard');
  const [notificationCount, setNotificationCount] = useState(0);

  useEffect(() => {
    fetchNotificationCount();
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      fetchNotificationCount();
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const path = location.pathname.split('/').pop() || 'dashboard';
    setSelectedMenu(path);
  }, [location]);

  const fetchNotificationCount = async () => {
    try {
      const [loansResponse, requestsResponse, closedLoansResponse] = await Promise.all([
        api.get('/officer/loans', { params: { officerId: user.userId } }),
        api.get('/officer/customer-requests', { params: { officerId: user.userId } }),
        api.get('/officer/loans/closed-by-admin', { params: { officerId: user.userId } }).catch(() => ({ data: [] })),
      ]);
      
      const loans = loansResponse.data || [];
      const requests = requestsResponse.data || [];
      const closedLoans = closedLoansResponse.data || [];
      
      // Get viewed notifications from localStorage
      const viewedNotifications = JSON.parse(localStorage.getItem(`viewedNotifications_${user.userId}`) || '[]');
      
      // Count new notifications (recently approved/rejected) that haven't been viewed
      const recentLoans = loans.filter(loan => {
        const isRecent = (loan.status === 'ACTIVE' || loan.status === 'APPROVED' || loan.status === 'REJECTED') &&
          loan.updatedAt && 
          new Date(loan.updatedAt) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000); // Last 7 days
        const isViewed = viewedNotifications.includes(`loan-${loan.id}`);
        return isRecent && !isViewed;
      });
      
      const recentRequests = requests.filter(request => {
        const isRecent = (request.status === 'APPROVED' || request.status === 'REJECTED' || request.status === 'APPROVED_FOR_EDIT') &&
          request.reviewedAt &&
          new Date(request.reviewedAt) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000); // Last 7 days
        const isViewed = viewedNotifications.includes(`request-${request.id}`);
        return isRecent && !isViewed;
      });
      
      // Count loans closed by admin (recent ones)
      const recentClosedLoans = closedLoans.filter(loan => {
        const isRecent = loan.closedAt && 
          new Date(loan.closedAt) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000); // Last 7 days
        const isViewed = viewedNotifications.includes(`admin-closed-loan-${loan.id}`);
        return isRecent && !isViewed;
      });
      
      setNotificationCount(recentLoans.length + recentRequests.length + recentClosedLoans.length);
    } catch (error) {
      console.error('Error fetching notification count:', error);
    }
  };

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: 'dashboard' },
    { 
      text: 'Notifications', 
      icon: <NotificationsIcon />, 
      path: 'notifications',
      badgeCount: notificationCount,
      badgeColor: 'primary'
    },
    { text: 'Register Customer', icon: <PeopleIcon />, path: 'customers/register' },
    { text: 'Customer Management', icon: <PeopleIcon />, path: 'customers' },
    { text: 'Create Loan Application', icon: <DescriptionIcon />, path: 'loans' },
    { text: 'Create Loan for Existing Customer', icon: <DescriptionIcon />, path: 'loans/for-existing' },
    { text: 'My Loan Applications', icon: <ListAltIcon />, path: 'my-loans' },
    { text: 'Loans Closed by Admin', icon: <ListAltIcon />, path: 'loans-closed-by-admin' },
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuClick = (item) => {
    setSelectedMenu(item.path);
    navigate(`/officer/${item.path}`);
    if (isMobile) {
      setMobileOpen(false);
    }
    if (item.badgeCount !== undefined) {
      fetchNotificationCount();
    }
  };

  const drawer = (
    <Box>
      <Toolbar
        sx={{
          background: 'linear-gradient(135deg, #1976D2 0%, #0D47A1 100%)',
          color: 'white',
          minHeight: { xs: '56px', sm: '64px' },
        }}
      >
        <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 600 }}>
          Officer Panel
        </Typography>
      </Toolbar>
      <Divider />
      <Box sx={{ overflow: 'auto', mt: 1 }}>
          <List>
            {menuItems.map((item) => (
              <ListItem key={item.text} disablePadding>
                <ListItemButton
                  selected={selectedMenu === item.path}
                onClick={() => handleMenuClick(item)}
                sx={{
                  mx: 1,
                  mb: 0.5,
                  borderRadius: 2,
                  '&.Mui-selected': {
                    background: 'linear-gradient(135deg, #1976D2 0%, #0D47A1 100%)',
                    color: 'white',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #1565C0 0%, #0A3D91 100%)',
                    },
                    '& .MuiListItemIcon-root': {
                      color: 'white',
                    },
                    '& .MuiListItemText-primary': {
                      fontWeight: 600,
                    },
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.1)',
                  },
                  }}
                >
                <ListItemIcon
                  sx={{
                    color: selectedMenu === item.path ? 'white' : '#1976D2',
                    minWidth: 40,
                  }}
                >
                    {item.badgeCount !== undefined && item.badgeCount > 0 ? (
                      <Badge badgeContent={item.badgeCount} color={item.badgeColor || 'primary'}>
                        {item.icon}
                      </Badge>
                    ) : (
                      item.icon
                    )}
                  </ListItemIcon>
                <ListItemText 
                  primary={item.text}
                  primaryTypographyProps={{
                    fontSize: '0.95rem',
                  }}
                />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#F5F7FA' }}>
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          background: 'linear-gradient(135deg, #1976D2 0%, #0D47A1 100%)',
          boxShadow: '0 4px 20px rgba(13, 71, 161, 0.3)',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, fontWeight: 600 }}>
            Loan Officer Dashboard
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              mr: 2, 
              display: { xs: 'none', sm: 'block' },
              fontWeight: 500,
            }}
          >
            {user?.fullName}
          </Typography>
          <Button 
            color="inherit" 
            onClick={handleLogout} 
            startIcon={<LogoutIcon />}
            sx={{
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
              Logout
            </Box>
          </Button>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              borderRight: '1px solid rgba(25, 118, 210, 0.1)',
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              borderRight: '1px solid rgba(25, 118, 210, 0.1)',
            },
          }}
          open
        >
          {drawer}
      </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          width: { md: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
        }}
      >
        <Toolbar />
        <Routes>
          <Route path="" element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<OfficerHome />} />
          <Route path="notifications" element={<Notifications onNotificationsViewed={fetchNotificationCount} />} />
          <Route path="customers/register" element={<CustomerRegistration />} />
          <Route path="customers" element={<CustomerManagement />} />
          <Route path="customers/:id" element={<CustomerManagement />} />
          <Route path="loans" element={<LoanApplication />} />
          <Route path="loans/for-existing" element={<LoanApplicationForExisting />} />
          <Route path="loans/active" element={<ActiveLoans />} />
          <Route path="loans/rejected" element={<RejectedLoans />} />
          <Route path="loans/closed" element={<ClosedLoans />} />
          <Route path="loans/pending-verified" element={<PendingVerifiedLoans />} />
          <Route path="my-loans" element={<MyLoanApplications />} />
          <Route path="loans/:id/schedule" element={<LoanSchedule />} />
          <Route path="loans-closed-by-admin" element={<LoansClosedByAdmin />} />
        </Routes>
      </Box>
    </Box>
  );
};

export default OfficerDashboard;

